/**
 * The reviewer's per-detection verdict.
 *
 * Sign-off is per observation, not per file: one inspection routinely contains a critical
 * fracture next to a cosmetic deposit, and a single review-wide status cannot express that.
 *
 * These values are stored on the REVIEW (the approved detection list), never written back
 * into ocr.json — the OCR output has to stay exactly as the machine produced it so the two
 * can be compared.
 */

export type DetectionStatus = "Draft" | "InReview" | "Approved" | "Stale";

export type ConditionRating = "Excellent" | "Good" | "Fair" | "Poor" | "Critical";

export type RiskRating = "Low" | "Medium" | "High" | "Critical";

export interface DetectionAssessment {
  detectionStatus?: DetectionStatus;
  conditionRating?: ConditionRating;
  riskRating?: RiskRating;
}

/** New detections start unreviewed. */
export const DEFAULT_DETECTION_STATUS: DetectionStatus = "Draft";

export const DETECTION_STATUS_OPTIONS: { value: DetectionStatus; labelKey: string }[] = [
  { value: "Draft", labelKey: "comparison.review.status.draft" },
  { value: "InReview", labelKey: "comparison.review.status.in-review" },
  { value: "Approved", labelKey: "comparison.review.status.approved" },
  { value: "Stale", labelKey: "comparison.review.status.stale" },
];

export const CONDITION_RATING_OPTIONS: { value: ConditionRating; labelKey: string }[] = [
  { value: "Excellent", labelKey: "detection.condition.excellent" },
  { value: "Good", labelKey: "detection.condition.good" },
  { value: "Fair", labelKey: "detection.condition.fair" },
  { value: "Poor", labelKey: "detection.condition.poor" },
  { value: "Critical", labelKey: "detection.condition.critical" },
];

export const RISK_RATING_OPTIONS: { value: RiskRating; labelKey: string }[] = [
  { value: "Low", labelKey: "detection.risk.low" },
  { value: "Medium", labelKey: "detection.risk.medium" },
  { value: "High", labelKey: "detection.risk.high" },
  { value: "Critical", labelKey: "detection.risk.critical" },
];

/** MUI colour for a rating chip, so the same word looks the same everywhere it appears. */
export function conditionColor(
  rating?: ConditionRating
): "success" | "info" | "warning" | "error" | "default" {
  switch (rating) {
    case "Excellent":
      return "success";
    case "Good":
      return "info";
    case "Fair":
      return "warning";
    case "Poor":
    case "Critical":
      return "error";
    default:
      return "default";
  }
}

export function riskColor(rating?: RiskRating): "success" | "warning" | "error" | "default" {
  switch (rating) {
    case "Low":
      return "success";
    case "Medium":
      return "warning";
    case "High":
    case "Critical":
      return "error";
    default:
      return "default";
  }
}

export function statusColor(
  status?: DetectionStatus
): "success" | "info" | "warning" | "default" {
  switch (status) {
    case "Approved":
      return "success";
    case "InReview":
      return "info";
    case "Stale":
      return "warning";
    default:
      return "default";
  }
}

/**
 * Distance plus main code — the identity that survives a redo.
 *
 * MUST stay byte-identical to InspectionReviewProvider.StableKeyFor on the server: it is what
 * matches an OCR detection on screen to its stored assessment, so a format drift would
 * silently orphan every reviewer verdict.
 */
export function stableKeyFor(
  distance: number | undefined,
  mainCode: string | undefined,
  timeSeconds?: number
): string {
  const station = Number(distance ?? 0).toFixed(2);
  const code = (mainCode ?? "").trim().toUpperCase();

  // Station and code alone are NOT an identity. An inspection legitimately reports the same code
  // twice at the same metre -- the analysis output routinely finds one defect at 1:15 and another
  // at 1:37, both at 2.43 m -- and collapsing them meant approving one row wrote over the other,
  // both tick boxes moved together, and the two shared a single frame file. The video position is
  // what tells them apart, rounded to the second so float noise in the analysis output does not
  // hand the same damage two identities.
  //
  // Optional, so a caller with no time to offer (an ocr.json display row, whose timestamp is a
  // clock string) keeps the original two-part key rather than silently keying on a missing value.
  return timeSeconds === undefined || timeSeconds === null
    ? `${station}|${code}`
    : `${station}|${code}|${Math.round(Number(timeSeconds))}`;
}
